'use client'

import { flip, offset, shift } from '@floating-ui/react-dom'
import {
  ClickAwayListener,
  Unstable_Popup as Popup,
  type PopupMiddleware,
  type PopupProps,
} from '@mui/base'
import { clsx } from 'clsx'
import {
  cloneElement,
  type ComponentProps,
  Fragment,
  type JSX,
  type ReactElement,
  type ReactNode,
  useRef,
} from 'react'

import { useTooltipToggle } from './useTooltipToggle'

type TooltipOwnProps = {
  /**
   * @default false
   */
  arrow?: boolean

  children: ReactElement<ComponentProps<'div'>>

  clickTrigger?: boolean

  open?: boolean

  /**
   * @default bottom
   */
  placement?: 'bottom' | 'left' | 'right' | 'top'

  title: ReactNode
}

type TooltipProps = Omit<
  PopupProps,
  keyof TooltipOwnProps | 'middleware' | 'offset'
> &
  TooltipOwnProps

const TOOLTIP_OFFSET = 12

const middleware: PopupMiddleware[] = [
  offset(TOOLTIP_OFFSET),
  flip(),
  shift(),
  {
    fn: ({ elements, placement }): NonNullable<unknown> => {
      elements.floating.dataset.placement = placement

      return {}
    },
    name: 'placement',
  },
]

/**
 * Tooltips display informative text when users hover over, focus on, or tap an element.
 */
export const Tooltip = (props: TooltipProps): JSX.Element => {
  const {
    anchor,
    arrow = false,
    children,
    className,
    clickTrigger = false,
    open: openProp,
    placement = 'bottom',
    ref,
    title,
    ...rest
  } = props

  const childRef = useRef(null)

  const {
    onTooltipClose,
    onTooltipOpen,
    onTooltipPointerLeave,
    onTooltipToggle,
    tooltipOpen,
  } = useTooltipToggle(Boolean(openProp))

  if (process.env.NODE_ENV !== 'production') {
    if (anchor === undefined && children.type === Fragment) {
      console.warn(
        'Tooltip children should not be a Fragment but an interactive element',
      )
    }

    if (anchor !== undefined && 'ref' in children.props) {
      console.warn('Children element has a ref already')
    }
  }

  return (
    <>
      {clickTrigger ? (
        <ClickAwayListener onClickAway={onTooltipClose}>
          {cloneElement(children, {
            onClick: onTooltipToggle,
            ref: childRef,
          })}
        </ClickAwayListener>
      ) : (
        // eslint-disable-next-line react-hooks/refs -- don't reference, refactor if touched
        cloneElement(children, {
          onBlur: onTooltipClose,
          onFocus: onTooltipOpen,
          onPointerEnter: onTooltipToggle,
          onPointerLeave: onTooltipPointerLeave,
          ref: childRef,
        })
      )}

      <Popup
        {...rest}
        // eslint-disable-next-line react-hooks/refs -- don't reference, refactor if touched
        anchor={anchor ?? childRef.current}
        className={clsx('tooltip', className)}
        middleware={middleware}
        open={tooltipOpen}
        placement={placement}
        ref={ref}
      >
        {arrow ? <span className="tooltip__arrow" /> : null}
        {title}
      </Popup>
    </>
  )
}
